import { iif, Subject } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { map, skipWhile, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FormControl, Validators } from '@angular/forms';

import { UserService } from '@core/services/user/user.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';

import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';

import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { ZerosixCreateAccountModalComponent } from '@shared/modals/zerosix/zerosix-create-account-modal/zerosix-create-account-modal.component';
import { ZerosixError } from '@core/models/zerosix.model';
import { ZEROSIX_ERRORS } from '@config/values/zerosix-errors.values';

@Component({
  selector: 'app-zerosix-link-account-modal',
  templateUrl: './zerosix-link-account-modal.component.html',
  styles: [
    `
      // Permettre à la liste des pays de déborder de la modal sur les petits écrans
      :host {
        @media (max-width: 767px) {
          // Sélectionner la div modale principale
          .fixed.left-0.right-0 {
            overflow: visible !important;
          }

          // Sélectionner le conteneur blanc de la modal
          .rounded-t-3xl.bg-white {
            overflow: visible !important;
          }
        }
      }
    `,
  ],
})
export class ZerosixLinkAccountModalComponent
  extends ModalScrollBlockBase
  implements OnInit, OnDestroy
{
  private _destroyerRef = new Subject<boolean>();

  static handle = 'zerosix-link-account-modal';

  isDesktop$ = this.settingsSelector.selectDeviceIsDesktopScreen;

  index$ = this.modalSelector
    .selectModal(ZerosixLinkAccountModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  phoneForm = new FormControl<string>('', {
    validators: [Validators.required],
  });
  authForm = new FormControl<string>('', {
    validators: [Validators.required],
  });

  errorMessage?: string;
  phoneErrorMessage?: string;
  authCodeErrorMessage?: string;

  isValidating = false;

  step: 'link-form' | 'verification-code' | 'confirmation' = 'link-form';

  get isStepSubmitButtonDisabled() {
    if (this.isValidating) {
      return true;
    }
    if (this.step === 'verification-code') {
      return this.isValidating || this.authForm.invalid;
    }
    return this.phoneForm.invalid;
  }

  constructor(
    private authSelector: AuthStoreSelector,
    private modalSelector: ModalStoreSelector,
    private readonly userService: UserService,
    private readonly modalsService: ModalsService,
    private settingsSelector: SettingsStoreSelector,
    private readonly zerosixService: ZerosixService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.authSelector.selectUser
      .pipe(
        takeUntil(this._destroyerRef),
        skipWhile(user => !user),
        tap(user => this.setPhoneNumber(user?.phone)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
    super.ngOnDestroy();
  }

  close(): void {
    this.modalsService.close(ZerosixLinkAccountModalComponent.handle);
  }

  onSubmit(): void {
    if (this.isValidating) {
      return;
    }

    const phoneNumber = this.phoneForm.value;
    if (phoneNumber && this.phoneForm.valid && this.step !== 'verification-code') {
      this.isValidating = true;
      this.phoneErrorMessage = undefined;
      this.authCodeErrorMessage = undefined;
      this.errorMessage = undefined;

      this.zerosixService
        .isZerosixOtpIdentificationRequired$()
        .pipe(
          switchMap(isOtpRequired => {
            return iif(
              () => isOtpRequired,
              this.zerosixService.sendOtp$(phoneNumber),
              this.zerosixService.attachUserByPhone$({
                phoneNumber,
              }),
            ).pipe(map(() => isOtpRequired));
          }),
          tap(isOtpRequired =>
            isOtpRequired ? this.goToVerificationCodeStep() : this.goToConfirmationStep(),
          ),
        )
        .subscribe({
          error: error => {
            this.handleErrorResponse(error.error);
          },
          complete: () => (this.isValidating = false),
        });
      return;
    }

    const authValue = this.authForm.value;
    if (this.authForm.invalid || !authValue) {
      return;
    }
    this.isValidating = true;
    this.phoneErrorMessage = undefined;
    this.authCodeErrorMessage = undefined;
    this.errorMessage = undefined;

    this.zerosixService
      .attachUserWithOtp$(authValue)
      .pipe(
        tap(user => this.userService.setUser(user)),
        tap(() => this.goToConfirmationStep()),
      )
      .subscribe({
        error: error => {
          this.isValidating = false;
          this.handleErrorResponse(error.error);
        },
        complete: () => (this.isValidating = false),
      });
  }

  openCreateLoyaltyAccountModal(): void {
    this.close();
    this.modalsService.open(ZerosixCreateAccountModalComponent.handle);
  }

  private handleErrorResponse(error: ZerosixError): void {
    if (error.class === 'ZerosixException') {
      if (error.code === 'ZEROSIX_MEMBER_NOT_FOUND') {
        this.phoneErrorMessage = 'zerosix.link-account.errors.account-not-found';
      }
      this.errorMessage = ZEROSIX_ERRORS[error.code];
      return;
    }
    this.errorMessage = 'errors.generic-error';
  }

  private goToVerificationCodeStep(): void {
    this.step = 'verification-code';
  }

  private goToConfirmationStep(): void {
    this.step = 'confirmation';
  }

  private setPhoneNumber(phoneNumber?: string): void {
    this.phoneForm.setValue(phoneNumber ?? '');
  }
}
